import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Token Refresh Service
 *
 * Monitors token expiration and handles automatic logout before token expires.
 * Best practice: Warn user 5 minutes before expiration.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private checkInterval?: number;
  private readonly CHECK_INTERVAL_MS = 60000; // Check every minute
  private readonly WARNING_THRESHOLD_MS = 5 * 60 * 1000; // Warn 5 minutes before expiration

  /**
   * Start monitoring token expiration
   */
  startMonitoring(): void {
    // Clear any existing interval
    this.stopMonitoring();

    // Check immediately
    this.checkTokenExpiration();

    // Set up periodic check
    this.checkInterval = window.setInterval(() => {
      this.checkTokenExpiration();
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  /**
   * Check if token is expired or about to expire
   */
  private checkTokenExpiration(): void {
    const idToken = this.authService.getIdToken();
    if (!idToken) {
      return;
    }

    try {
      // Decode ID token to get expiration
      const payload = this.decodeJWT(idToken);
      const exp = payload.exp;

      if (!exp) {
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = (exp - now) * 1000; // Convert to milliseconds

      // If token is expired, logout
      if (timeUntilExpiration <= 0) {
        console.warn('🔥 Token expired - logging out');
        this.authService.logout();
        this.router.navigate(['/login'], {
          queryParams: { error: 'Your session has expired. Please login again.' },
        });
        this.stopMonitoring();
        return;
      }

      // If token expires soon, show warning (could emit event or show notification)
      if (timeUntilExpiration <= this.WARNING_THRESHOLD_MS) {
        const minutesLeft = Math.floor(timeUntilExpiration / 60000);
        console.warn(`⚠️ Token expires in ${minutesLeft} minutes`);
        // TODO: Emit event or show notification to user
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
    }
  }

  /**
   * Decode JWT token payload
   */
  private decodeJWT(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return {};
    }
  }
}
